/**
 * The top of the funnel: what the room guessed, who is checking each guess, and
 * what held up. A hunch is not a finding — it is a question with the searches
 * that would answer it — so the table reads as a checklist, supported first.
 */
import { useMemo } from 'react';
import { CARD } from './tableLayout';
import type { BoardroomHunches, BoardroomHunchRow } from '../../../types';
import MissingList, { type OnDig } from './MissingList';
import DataTable from './DataTable';
import type { Column } from './tableColumns';
import { useReportSeen } from '../useSeen';
import NewHere from './NewHere';
import { checkLabel, findingsLabel, hunchStateTone } from './hunchView';

export function hunchColumns(onOpen: (id: number) => void): Column<BoardroomHunchRow>[] {
  return [
    {
      key: 'hunch',
      label: 'Question',
      width: 36,
      always: true,
      value: r => r.title,
      cell: r => (
        <>
          <button type="button" onClick={() => onOpen(r.hunch_id)} className="text-gray-800 dark:text-gray-200 hover:underline text-left">
            {r.title}
          </button>
          {r.ideas.length > 0 && (
            <div className="text-[11px] text-indigo-600 dark:text-indigo-400">idea: {r.ideas.map(i => i.title).join(', ')}</div>
          )}
        </>
      ),
    },
    {
      key: 'state',
      label: 'State',
      width: 10,
      value: r => r.state,
      cell: r => <span className={`text-[11px] px-1.5 py-0.5 rounded ${hunchStateTone(r.state)}`}>{r.state}</span>,
    },
    {
      key: 'check',
      label: 'Checked for',
      width: 12,
      value: r => checkLabel(r.check),
      cell: r => <span className="text-gray-500">{checkLabel(r.check)}</span>,
    },
    {
      key: 'found',
      label: 'Found',
      width: 20,
      value: r => r.findings.length,
      cell: r =>
        r.findings.length === 0 ? (
          <span className="text-gray-400 italic">nothing yet</span>
        ) : (
          <span className="text-gray-600 dark:text-gray-300" title={r.findings.map(f => f.title).join('\n')}>
            {findingsLabel(r)}
          </span>
        ),
    },
    {
      key: 'searches',
      label: 'Searches',
      width: 22,
      sortable: false,
      value: r => r.searches.join(' / '),
      cell: r => <span className="text-xs text-gray-400">{r.searches.join(' · ') || '-'}</span>,
    },
  ];
}

export default function HunchesReport({
  data,
  onOpen,
  onDig,
  digging,
  unseen,
  onSeen,
  roomName,
}: {
  data: BoardroomHunches;
  onOpen: (id: number) => void;
  onDig: OnDig;
  digging: boolean;
  unseen?: ReadonlySet<number>;
  onSeen?: (ids: number[]) => void;
  roomName?: string;
}) {
  const columns = useMemo(() => hunchColumns(onOpen), [onOpen]);
  const empty = data.totals.hunches === 0;
  const seen = useReportSeen(unseen, onSeen, [
    ...data.rows.map(r => r.hunch_id),
    ...data.missing.map(m => m.item_id),
  ]);
  return (
    <div className="flex flex-col gap-3 min-h-0">
      <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={seen.next} onClearAll={seen.readAll} />
      {!empty && (
        <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="hunch-totals">
          {data.totals.hunches} hunches · {data.totals.supported} supported · {data.totals.dead} dead · {data.totals.open} open
        </div>
      )}
      {!empty && (
        <div className={CARD}>
          <DataTable
            table="hunches"
            room={roomName}
            seen={seen}
            columns={columns}
            rows={data.rows}
            rowKey={r => `hunch-${r.hunch_id}`}
            rowClass={r => (r.state === 'dead' ? 'opacity-60' : '')}
            rowData={r => ({ 'data-state': r.state })}
          />
        </div>
      )}
      <MissingList missing={data.missing} empty={empty} onOpen={onOpen} onDig={onDig} digging={digging} seen={seen} />
    </div>
  );
}
